/**
 * 房间顶栏：房间码 / 人数 / 盲注 / 房主标记 / 离开。
 *
 * 房间码为什么做成可点开的面板（docs/DEVICES.md R3）：
 * 分发路径**绝不能只有「点链接」**——微信随时可能屏蔽域名，而 6 位码用任何渠道
 * （文字、口头、截图）都能传。所以「复制房间码」必须比「复制链接」更显眼。
 *
 * 顶栏高度只有 48px（`TOPBAR_H`），所以这里只放最必要的三样：
 * 房间码、人数、离开。盲注等信息收进展开面板。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { deckVariantOf, type PlayerView } from '@poker/shared'

import { useUiStore } from '../store/index.js'
import { AudioSettings } from '../fx/AudioSettings.js'
import { selectIsActor, selectNetView, useNetStore } from '../transport/applyMessage.js'

import { EndGameButton } from './Settlement.js'
import { TopUpQueueBadge } from './TopUp.js'
import { TransferLogButton, TransferQueueBadge } from './Transfer.js'
import { blindsText, groupRoomCode, isHost, leaveOpensSeatSheet, seatedCount } from './roomDerive.js'
import { useLeaveRoom } from './useLeaveRoom.js'
import './room.css'

export interface RoomHeaderProps {
  /** 离开房间后的去向（一般是「回大厅」）。发 `leaveRoom` 之后调用。 */
  readonly onLeave?: () => void
  /** 覆盖 store 里的视图（回放 / storybook / 单测用）；省略就订阅 store。 */
  readonly view?: PlayerView | null
}

export function RoomHeader({ onLeave, view: viewProp }: RoomHeaderProps): ReactNode {
  const storeView = useNetStore(selectNetView)
  const isActor = useNetStore(selectIsActor)
  const view = viewProp === undefined ? storeView : viewProp
  const showToast = useUiStore((s) => s.showToast)
  const openSheet = useUiStore((s) => s.openSheet)
  const leaveRoom = useLeaveRoom(onLeave, view)
  const [openInvite, setOpenInvite] = useState(false)
  const [armed, setArmed] = useState(false)
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inviteButtonRef = useRef<HTMLButtonElement | null>(null)
  const invitePanelRef = useRef<HTMLDivElement | null>(null)

  useEffect(
    () => () => {
      if (armTimer.current !== null) clearTimeout(armTimer.current)
    },
    [],
  )

  const closeInvite = useCallback((): void => {
    setOpenInvite(false)
    requestAnimationFrame(() => inviteButtonRef.current?.focus())
  }, [])

  // portal 改变了 DOM 顺序：面板视觉上在顶栏下方，DOM 却挂在 shell 末尾。
  // 打开时把焦点送进面板，Esc /「收起」再送回触发按钮，键盘顺序才和画面一致。
  useEffect(() => {
    if (!openInvite) return undefined
    const frame = requestAnimationFrame(() => invitePanelRef.current?.focus())
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      closeInvite()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [closeInvite, openInvite])

  const code = view?.room.code ?? null

  const copy = useCallback((): void => {
    if (code === null) return
    // iOS 微信里 clipboard API 可能不存在；能力检测后退化成「长按房间码可以复制」
    const clip = globalThis.navigator?.clipboard
    if (clip?.writeText === undefined) {
      showToast('长按房间码可以复制', 'info')
      return
    }
    void clip.writeText(code).then(
      () => showToast('已复制', 'success'),
      () => showToast('复制失败，长按房间码可以复制', 'warn'),
    )
  }, [code, showToast])

  /**
   * 坐着、这一页有行动权：「离开」打开「我的座位」面板（暂离 / 换座 / 起立 / 离开房间）。
   * 想起立的人以前只能点这里，而它离开的是整个房间 —— 回大厅、房间从续局卡里消失、
   * 还得重新输房间码。面板本身就是离开房间的第二步，所以这里不再两段式。
   */
  const seatSheet = leaveOpensSeatSheet(view, isActor)

  /** 旁观者 / 影子页：两段式离开。退出牌局不可撤销，单击就走太容易误触。 */
  const leave = useCallback((): void => {
    if (seatSheet) {
      openSheet('seatSelf')
      return
    }
    if (!armed) {
      setArmed(true)
      armTimer.current = setTimeout(() => setArmed(false), 3000)
      return
    }
    if (armTimer.current !== null) clearTimeout(armTimer.current)
    leaveRoom()
  }, [armed, leaveRoom, openSheet, seatSheet])

  const seated = seatedCount(view)
  const maxSeats = view?.room.config.maxSeats ?? 0
  const host = isHost(view)
  const shortDeck = view !== null && deckVariantOf(view.room.config) === 'shortDeck'

  // ★ 面板 portal 到 .app-shell 上，而不是留在顶栏里，也不是挂到 body：
  //   - 留在顶栏里：顶栏是 z-index:130 的层叠上下文，面板写多大的 z-index 都出不了这个壳，
  //     断线条（--z-banner 140）正好横在它顶上，sheet 级浮层也都压着它。
  //   - 挂到 body：.app-shell 是 position:fixed —— fixed 本身就是一个层叠上下文，toast(300)、
  //     sheet(200) 全在它**里面**；body 上一个 z:199 的面板会压在整个 shell 之上，连 toast
  //     和「结束牌局」的确认 sheet 都盖住。而且 club 主题的色板变量写在 .app-shell 上，
  //     出了 shell 面板就掉回默认色。
  //   挂到 shell 根上：同一套变量、同一个层叠上下文（room.css 里 z 取 --z-sheet − 1：压过
  //   断线条与抽屉，让位于 sheet 与 toast），绝对定位以 shell 为参照，几何与顶栏对齐。
  //   没有 document 的环境（node 测试 / SSR）原地渲染，断言照常。
  const doc = (globalThis as { document?: Document }).document
  const portalHost = doc?.querySelector<HTMLElement>('.app-shell') ?? doc?.body ?? null
  const panel = openInvite ? (
    <div
      id="room-invite-panel"
      ref={invitePanelRef}
      className="invite"
      role="dialog"
      aria-label="房间"
      tabIndex={-1}
    >
      <div className="invite__code num selectable">{groupRoomCode(code)}</div>
      {/* 怎么把码传出去，只在建房后的房间码屏说一次。这里码就在眼前、
          主按钮写着「复制房间码」，再写一行提示就是同一件事说第二遍 */}
      <dl className="invite__facts">
        <div>
          <dt>盲注</dt>
          <dd className="num">{blindsText(view?.room.config ?? null)}</dd>
        </div>
        <div>
          <dt>买入</dt>
          <dd className="num">{view?.room.config.startingStack ?? '—'}</dd>
        </div>
        <div>
          <dt>人数</dt>
          <dd className="num">
            {seated}/{maxSeats}
          </dd>
        </div>
      </dl>
      {/* 短牌规则全客户端只在这里说一次：真会被误解的只有这几条 */}
      {shortDeck && <p className="invite__rule">短牌：去掉 2–5，同花大于葫芦，A-6-7-8-9 是最小顺子</p>}
      <AudioSettings />
      <div className="invite__row">
        <button type="button" className="btn btn--primary" onClick={copy}>
          复制房间码
        </button>
        <button type="button" className="btn btn--ghost" onClick={closeInvite}>
          收起
        </button>
      </div>

      {/* ★ 结束牌局不可逆，所以收在面板里、点开还要再确认一次。
          放在牌桌上任何一颗常驻按钮里都迟早会被误触。 */}
      {host && (
        <div className="invite__row">
          <EndGameButton />
        </div>
      )}
    </div>
  ) : null

  return (
    <>
      <button
        ref={inviteButtonRef}
        type="button"
        className="roomcode"
        aria-expanded={openInvite}
        aria-controls={openInvite ? 'room-invite-panel' : undefined}
        aria-label={code === null ? '房间码加载中' : `房间码 ${code}，点击邀请朋友`}
        onClick={() => setOpenInvite((v) => !v)}
      >
        <span className="roomcode__val num selectable">{groupRoomCode(code)}</span>
        <span className="roomcode__caret" aria-hidden="true">
          {openInvite ? '▴' : '▾'}
        </span>
      </button>

      <span className="roomhead__meta">
        <span className="num">
          {seated}/{maxSeats}
        </span>
        {/* 只标非默认玩法：标准房什么都不显示 */}
        {shortDeck && <span className="badge badge--variant">短牌</span>}
        {host && <span className="badge badge--host">房主</span>}
      </span>

      {/* ★ 买分审批只在顶栏挂一个角标，绝不弹窗：房主可能正在自己的回合上，
          一个模态盖住牌桌会让他直接超时。没有申请时它完全不出现。 */}
      <TopUpQueueBadge view={view} />

      {/* 有人向我求购。同样只是角标，绝不弹窗：被求购的人可能正在自己的回合上 */}
      <TransferQueueBadge view={view} />

      {/* 转账记录**全员可见**：谁转给谁、多少、什么时候，是防 chip dumping 的唯一手段 */}
      <TransferLogButton view={view} />

      <button
        type="button"
        className={armed && !seatSheet ? 'btn btn--leave is-armed' : 'btn btn--leave'}
        onClick={leave}
        style={{ marginInlineStart: 'auto' }}
        {...(seatSheet ? { 'aria-haspopup': 'dialog' as const } : {})}
      >
        {armed && !seatSheet ? '确认离开' : '离开'}
      </button>

      {panel !== null && (portalHost === null ? panel : createPortal(panel, portalHost))}
    </>
  )
}

export default RoomHeader
